import React, { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Container,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { DatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { endOfDay, format, parse, startOfDay, subDays } from "date-fns";
import { es } from "date-fns/locale";
import AssessmentIcon from "@material-ui/icons/Assessment";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import MetricsCards from "../../components/CampaignShippingReports/MetricsCards";
import ChartsSection from "../../components/CampaignShippingReports/ChartsSection";
import EstimationsPanel from "../../components/GroupCampaignShippingReports/EstimationsPanel";
import ShippingDetailTable from "../../components/GroupCampaignShippingReports/ShippingDetailTable";

class CustomDateFnsUtils extends DateFnsUtils {
  static SAFE_FORMAT = "dd'/'MM'/'yyyy";

  format(date, formatString) {
    if (!date) return "";
    try {
      return format(date, CustomDateFnsUtils.SAFE_FORMAT, { locale: es });
    } catch (error) {
      return super.format(date, formatString);
    }
  }

  parse(value, formatString) {
    if (!value || typeof value !== "string") return null;
    try {
      return parse(value, CustomDateFnsUtils.SAFE_FORMAT, new Date(), { locale: es });
    } catch (error) {
      return super.parse(value, formatString);
    }
  }
}

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.default,
    minHeight: 0,
    padding: theme.spacing(3)
  },
  mainPaper: {
    backgroundColor: theme.palette.background.paper,
    marginBottom: theme.spacing(3),
    padding: theme.spacing(3)
  },
  header: {
    alignItems: "center",
    display: "flex",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(3)
  },
  title: {
    color: theme.palette.primary.main,
    fontWeight: 600
  },
  filters: {
    marginBottom: theme.spacing(3)
  },
  loading: {
    alignItems: "center",
    display: "flex",
    justifyContent: "center",
    minHeight: 160
  }
}));

const GroupCampaignShippingReports = () => {
  const classes = useStyles();
  const [startDate, setStartDate] = useState(startOfDay(subDays(new Date(), 7)));
  const [endDate, setEndDate] = useState(endOfDay(new Date()));
  const [selectedCampaignId, setSelectedCampaignId] = useState("");
  const [campaigns, setCampaigns] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadCampaigns = async () => {
      try {
        const { data } = await api.get("/group-campaigns", {
          params: { pageNumber: 1, limit: 1000 }
        });
        setCampaigns(Array.isArray(data) ? data : []);
      } catch (err) {
        toastError(err);
      }
    };
    loadCampaigns();
  }, []);

  useEffect(() => {
    const loadMetrics = async () => {
      setLoading(true);
      try {
        const params = {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        };
        if (selectedCampaignId) params.campaignId = selectedCampaignId;
        const { data } = await api.get("/group-campaign-shipping-reports/metrics", {
          params
        });
        setMetrics(data);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    };
    loadMetrics();
  }, [endDate, selectedCampaignId, startDate]);

  return (
    <MuiPickersUtilsProvider utils={CustomDateFnsUtils} locale={es}>
      <Container maxWidth={false} className={classes.root}>
        <Paper className={classes.mainPaper}>
          <Box className={classes.header}>
            <AssessmentIcon color="primary" />
            <Typography variant="h4" className={classes.title}>
              Informe de envío · Grupos y canales
            </Typography>
          </Box>
          <Box className={classes.filters}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <DatePicker
                  label="Fecha Inicio"
                  value={startDate}
                  onChange={setStartDate}
                  format="dd/MM/yyyy"
                  fullWidth
                  inputVariant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <DatePicker
                  label="Fecha Fin"
                  value={endDate}
                  onChange={setEndDate}
                  format="dd/MM/yyyy"
                  fullWidth
                  inputVariant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  select
                  label="Campaña"
                  value={selectedCampaignId}
                  onChange={(event) => setSelectedCampaignId(event.target.value)}
                  fullWidth
                  variant="outlined"
                >
                  <MenuItem value="">Todas las campañas</MenuItem>
                  {campaigns.map((campaign) => (
                    <MenuItem key={campaign.id} value={campaign.id}>
                      {campaign.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </Box>
          {loading ? (
            <Box className={classes.loading}><CircularProgress /></Box>
          ) : (
            <>
              <MetricsCards metrics={metrics} reportType="groups" />
              <EstimationsPanel
                campaigns={campaigns}
                selectedCampaignId={selectedCampaignId}
                setSelectedCampaignId={setSelectedCampaignId}
              />
              <ChartsSection metrics={metrics} reportType="groups" />
              <ShippingDetailTable
                startDate={startDate}
                endDate={endDate}
                selectedCampaignId={selectedCampaignId}
              />
            </>
          )}
        </Paper>
      </Container>
    </MuiPickersUtilsProvider>
  );
};

export default GroupCampaignShippingReports;
